import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError, finalize, shareReplay } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { UserModel } from '../user.model';
import { Router } from '@angular/router';
import { environment } from '../environments/environment';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable({
  providedIn: 'root'
})


export class AuthService {
  private baseUrl = environment.apiBaseUrl + '/account'; // Ihr Backend-URL
  private accessUrl = environment.apiBaseUrl + '/access'; // Zugriffssteuerung
  private token: string | null = null;
  private jwtHelper = new JwtHelperService();
  private logoutTimer: any;
  private readonly ROLE_CLAIM = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';


  // ========================================================
  // OFFLINE-KULANZ
  //
  // Auf einer Anlage hält kein Netz 30 Minuten durch. Ohne diese
  // Regelung würde das fehlgeschlagene Token-Refresh den Monteur
  // ausloggen, obwohl alle Daten auf dem Gerät liegen – ein Arbeitstag
  // wäre nach einer halben Stunde vorbei.
  //
  // Der Grundsatz: die Sitzung beendet NUR eine echte Ablehnung des
  // Servers. Ein Netzwerkfehler heißt lediglich, dass wir ihn gerade
  // nicht fragen können.
  //
  // Sicherheitlich kostet das nichts: ein abgelaufenes JWT verschafft
  // keinen Serverzugriff – der Server prüft es selbst und weist es ab.
  // Offline erreichbar sind ausschliesslich Daten, die dieser Benutzer
  // vorher bewusst auf dieses Gerät geladen hat. Nach oben begrenzt ist
  // das Ganze durch die Laufzeit des Refresh-Tokens: läuft es ab, hilft
  // nur noch eine Anmeldung mit Passwort.
  // ========================================================

  /** Laufzeit des Refresh-Tokens am Server (AccountController: AddDays(7)). */
  private static readonly REFRESH_TOKEN_LAUFZEIT_MS = 7 * 24 * 60 * 60 * 1000;

  /**
   * Abstand zwischen zwei Refresh-Versuchen, nachdem einer am Netz
   * gescheitert ist.
   *
   * Nötig, weil `isAuthenticated()` aus Templates und Guards heraus bei
   * jedem Änderungslauf aufgerufen wird. Ohne Sperre würde daraus ohne Netz
   * ein Anfragensturm.
   */
  private static readonly REFRESH_ABSTAND_MS = 30_000;

  /** Läuft gerade ein Refresh? Verhindert parallele Versuche. */
  private refreshLaeuft = false;

  /** Frühester Zeitpunkt für den nächsten Refresh-Versuch. */
  private naechsterRefreshAb = 0;


  constructor(private http: HttpClient, private router: Router, private snackBar: MatSnackBar) { }

  login(email: string, password: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/login`, { email, password })
      .pipe(
        map((response: any) => {
          this.token = response.token;
          if (this.token) {
            const decodedToken = this.jwtHelper.decodeToken(this.token);
            const expiresAt = decodedToken.exp * 1000;
            localStorage.setItem('token', this.token);
            localStorage.setItem('tokenExpiry', expiresAt.toString()); // ← Echte Ablaufzeit

            if (response.refreshToken) {
              this.merkeRefreshToken(response.refreshToken);
            }

            // Refresh 2 Minuten vor Ablauf
            const refreshDelay = Math.max(0, expiresAt - Date.now() - 120000);
            this.setLogoutTimer(refreshDelay);
          }
          return response;
        }),
        catchError(this.handleError)
      );
  }

// Gleiche Änderung in refreshToken():



  refreshToken(): Observable<any> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      this.logout();
      return throwError('Kein Refresh Token vorhanden.');
    }

    this.refreshLaeuft = true;

    return this.http.post(`${this.baseUrl}/refresh-token`, { refreshToken })
      .pipe(
        map((response: any) => {
          this.refreshLaeuft = false;
          this.naechsterRefreshAb = 0;

          this.token = response.token;
          if (this.token) {
            const decodedToken = this.jwtHelper.decodeToken(this.token);
            const expiresAt = decodedToken.exp * 1000;
            localStorage.setItem('token', this.token);
            localStorage.setItem('tokenExpiry', expiresAt.toString());
            if (response.refreshToken) {
              this.merkeRefreshToken(response.refreshToken);
            }
            const refreshDelay = Math.max(0, expiresAt - Date.now() - 120000);
            this.setLogoutTimer(refreshDelay);
          }
          return response;
        }),
        catchError(err => {
          this.refreshLaeuft = false;

          /*
           * Nur eine ECHTE Ablehnung des Servers beendet die Sitzung.
           *
           * Vorher stand hier ein bedingungsloses logout() – und genau das
           * warf einen Monteur nach 30 Minuten ohne Netz aus der Anwendung,
           * obwohl sämtliche Daten auf dem Gerät lagen. Ein Netzwerkfehler
           * ist keine Ablehnung; er heißt nur, dass wir den Server gerade
           * nicht fragen können.
           */
          if (this.istServerAblehnung(err)) {
            this.logout();
          } else {
            // Erst nach einer Weile erneut klopfen, sonst wird aus dem
            // Aufruf in isAuthenticated() ein Anfragensturm.
            this.naechsterRefreshAb =
              Date.now() + AuthService.REFRESH_ABSTAND_MS;
          }

          return throwError(err);
        })
      );
  }

  /**
   * Unterscheidet „der Server sagt nein" von „der Server ist nicht zu
   * erreichen".
   *
   * Status 0 heißt, dass die Anfrage den Server nie erreicht hat; 408/502/503/504
   * sind Zeitüberschreitungen bzw. ein Proxy, der gerade nicht durchstellt.
   * Nur 400/401/403/404 sind eine Aussage über die Anmeldung selbst.
   * Ein 500 zählt bewusst NICHT dazu: ein Serverfehler soll niemanden
   * ausloggen, ein späterer Versuch kann durchgehen.
   */
  private istServerAblehnung(err: any): boolean {
    if (!(err instanceof HttpErrorResponse)) {
      return false;
    }

    return err.status === 400
      || err.status === 401
      || err.status === 403
      || err.status === 404;
  }

  /**
   * Refresh-Token samt Ausstellungszeitpunkt merken.
   *
   * Der Zeitpunkt begrenzt die Offline-Kulanz: ist das Refresh-Token älter
   * als seine Serverlaufzeit, hilft auch offline keine Nachsicht mehr – dann
   * muss man sich neu anmelden.
   */
  private merkeRefreshToken(refreshToken: string): void {
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('refreshTokenAusgestelltAm', Date.now().toString());
  }

  /**
   * Ist ein Refresh-Token vorhanden, das noch etwas taugt?
   *
   * Bestandssitzungen ohne gespeicherten Ausstellungszeitpunkt bekommen den
   * Zweifel zugesprochen – über sie entscheidet dann der Server beim ersten
   * erreichbaren Versuch.
   */
  private hatVerwendbaresRefreshToken(): boolean {
    if (!localStorage.getItem('refreshToken')) {
      return false;
    }

    const ausgestellt = Number(
      localStorage.getItem('refreshTokenAusgestelltAm') ?? 0
    );

    if (!ausgestellt) {
      return true;
    }

    return Date.now() - ausgestellt < AuthService.REFRESH_TOKEN_LAUFZEIT_MS;
  }

  /**
   * Ein laufendes Refresh, das sich mehrere Aufrufer teilen.
   *
   * Zwingend nötig, seit auch der AuthInterceptor bei einem 401 ein Refresh
   * anstösst: der Server TAUSCHT das Refresh-Token bei jedem Einlösen aus
   * (AccountController.RefreshToken entfernt das alte). Zwei parallele
   * Aufrufe mit demselben Token bedeuten also, dass der zweite abgelehnt
   * wird – und das würde den Benutzer ausloggen, obwohl alles in Ordnung ist.
   */
  private laufendesRefresh: Observable<any> | null = null;

  /**
   * Refresh, das sich mehrere gleichzeitige Aufrufer teilen. Nur der erste
   * Aufruf schickt wirklich eine Anfrage.
   */
  refreshTokenGeteilt(): Observable<any> {
    if (!this.laufendesRefresh) {
      this.laufendesRefresh = this.refreshToken().pipe(
        finalize(() => (this.laufendesRefresh = null)),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }

    return this.laufendesRefresh;
  }

  /** Ist das aktuelle Token abgelaufen? (Es kann trotzdem eine Sitzung geben – siehe Offline-Kulanz.) */
  istTokenAbgelaufen(): boolean {
    const expiry = localStorage.getItem('tokenExpiry');

    return !!expiry && Date.now() > parseInt(expiry);
  }

  /** Lohnt bei einem 401 der Versuch, die Sitzung über das Refresh-Token zu retten? */
  kannSitzungErneuern(): boolean {
    return this.hatVerwendbaresRefreshToken();
  }

  /**
   * Stösst ein Refresh an, sofern gerade keines läuft und die Sperre nach
   * einem Netzwerkfehler abgelaufen ist.
   */
  private stosseRefreshAn(): void {
    if (this.refreshLaeuft || Date.now() < this.naechsterRefreshAb) {
      return;
    }

    this.refreshTokenGeteilt().subscribe({
      next: () => { },

      // Über Ausloggen oder Weiterarbeiten hat refreshToken() bereits
      // entschieden – hier gibt es nichts mehr zu tun.
      error: () => { }
    });
  }

  private setLogoutTimer(duration: number) {
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
    }

    if (duration <= 0) {
      this.stosseRefreshAn();
      return;
    }

    this.logoutTimer = setTimeout(() => {
      this.stosseRefreshAn();
    }, duration);
  }

  private clearLogoutTimer() {
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
      this.logoutTimer = null;
    }
  }

  register(user: UserModel): Observable<any> {
    const token = this.getToken();
    if (!token) {
      return throwError('Keine Berechtigung für die Benutzerverwaltung.');
    }
    return this.http.post(`${this.baseUrl}/register`, user, {
      headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` })
    }).pipe(catchError(this.handleError));
  }

  updateUser(user: UserModel): Observable<any> {
    const token = this.getToken();
    return this.http.put(`${this.baseUrl}/update/${user.id}`, user, {
      headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` })
    }).pipe(catchError(this.handleError));
  }

  deleteUser(userId: string): Observable<any> {
    const token = this.getToken();
    return this.http.delete(`${this.baseUrl}/delete/${userId}`, {
      headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` })
    }).pipe(catchError(this.handleError));
  }

  getUsers(): Observable<UserModel[]> {
    const token = this.getToken();
    return this.http.get<UserModel[]>(`${this.baseUrl}/users`, {
      headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` })
    }).pipe(catchError(this.handleError));
  }

  getCurrentUser(): Observable<UserModel> {
    const token = this.getToken();
    return this.http.get<UserModel>(`${this.baseUrl}/currentuser`, {
      headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` })
    }).pipe(catchError(this.handleError));
  }

  resetPassword(userId: string): Observable<any> {
    const token = this.getToken();
    return this.http.put(`${this.baseUrl}/resetpassword/${userId}`, {}, {
      headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` })
    }).pipe(catchError(this.handleError));
  }

  forgotPassword(email: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/forgot-password`, { email }).pipe(
      catchError(this.handleError)
    );
  }

  changePassword(oldPassword: string, newPassword: string): Observable<any> {
    const token = this.getToken();
    return this.http.put(`${this.baseUrl}/changepassword`, { oldPassword, newPassword }, {
      headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` })
    }).pipe(catchError(this.handleError));
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    const expiry = localStorage.getItem('tokenExpiry');

    if (!token || !expiry) {
      return false;
    }

    const now = Date.now();

    if (now <= parseInt(expiry)) {
      // Timer nur setzen wenn noch nicht aktiv
      if (!this.logoutTimer) {
        const refreshDelay = Math.max(0, parseInt(expiry) - now - 120000);
        this.setLogoutTimer(refreshDelay);
      }
      return !this.jwtHelper.isTokenExpired(token);
    }

    /*
     * Das Token ist abgelaufen. Früher endete die Sitzung hier sofort – und
     * damit auch die Arbeit auf der Anlage, wo ein Refresh mangels Netz gar
     * nicht gelingen KANN.
     *
     * Jetzt gilt: solange ein brauchbares Refresh-Token vorliegt, bleibt die
     * Sitzung bestehen und es wird im Hintergrund weiter versucht. Beendet
     * wird sie erst, wenn der Server erreichbar ist und ablehnt (dann greift
     * refreshToken() bzw. der 401 im AuthInterceptor) oder wenn das
     * Refresh-Token seine Laufzeit überschritten hat.
     *
     * Der Zugriff auf den Server wächst dadurch nicht: das abgelaufene JWT
     * weist der Server ohnehin ab. Erreichbar bleiben nur die Daten, die
     * dieser Benutzer vorher bewusst auf dieses Gerät geladen hat.
     */
    if (this.hatVerwendbaresRefreshToken()) {
      this.stosseRefreshAn();
      return true;
    }

    this.logout();
    return false;
  }

  /** Alle Rollen/Gruppen des Benutzers als Array (das JWT-Claim kann String oder Array sein). */
  getRoles(): string[] {
    const token = this.getToken();
    if (!token || !this.isAuthenticated()) {
      return [];
    }
    const decodedToken = this.jwtHelper.decodeToken(token);
    const role = decodedToken?.[this.ROLE_CLAIM];
    if (!role) {
      return [];
    }
    return Array.isArray(role) ? role : [role];
  }

  hasRole(role: string): boolean {
    return this.getRoles().includes(role);
  }

  isSuperAdmin(): boolean {
    return this.hasRole('SuperAdmin');
  }

  isAdmin(): boolean {
    // SuperAdmin ist eine Obermenge von Admin.
    return this.hasRole('Admin') || this.isSuperAdmin();
  }

  isCommercial(): boolean {
    return this.hasRole('Kaufmaennisch');
  }

  /** Ressourcenplanung sehen nur SuperAdmin und die kaufmännische Gruppe. */
  canSeeResourcePlanning(): boolean {
    return this.isSuperAdmin() || this.isCommercial();
  }

  /** Rollen/SuperAdmin vergeben darf nur der SuperAdmin. */
  canManageRoles(): boolean {
    return this.isSuperAdmin();
  }

  isBauleiter(): boolean {
    return this.hasRole('Bauleiter');
  }

  isMontage(): boolean {
    return this.hasRole('Montage');
  }

  isVorarbeiter(): boolean {
    return this.hasRole('Vorarbeiter');
  }

  isProjektleiterKaufmann(): boolean {
    return this.hasRole('ProjektleiterKaufmann');
  }

  /**
   * Preise/Stundensätze (Euro) SEHEN. Jede Management-/Bauleiter-/Kaufmann-Rolle darf das –
   * der Vorarbeiter bewusst NICHT. (Server entfernt Preise zusätzlich hart aus den Daten.)
   */
  canSeePrices(): boolean {
    return this.isSuperAdmin() || this.isAdmin() || this.isCommercial()
      || this.isProjektleiterKaufmann() || this.isBauleiter();
  }

  /** Stammkunden anlegen/verwalten darf nur der SuperAdmin. */
  canManageStammkunden(): boolean {
    return this.isSuperAdmin();
  }

  /**
   * "Reiner" Vorarbeiter: nur die Feld-Rolle Vorarbeiter, keine Leitungs-/Büro-Rolle.
   * Dieser sieht bewusst NUR das Lager und das Ticketsystem ("Meine Einsätze") –
   * keine Stammkunden-Verwaltung, keine Wartung, keine Planung.
   */
  isFieldVorarbeiter(): boolean {
    return this.isVorarbeiter()
      && !this.isSuperAdmin() && !this.isAdmin() && !this.isCommercial()
      && !this.isProjektleiterKaufmann() && !this.isBauleiter();
  }

  /** Wer darf den Stammkunden-Verwaltungsbereich betreten (Sichtbarkeit wird serverseitig gescopt). */
  canAccessStammkunden(): boolean {
    // Der reine Vorarbeiter arbeitet nur über das Ticketsystem, nicht in der Verwaltung.
    if (this.isFieldVorarbeiter()) {
      return false;
    }
    return this.isSuperAdmin() || this.isAdmin() || this.isCommercial()
      || this.isProjektleiterKaufmann() || this.isBauleiter() || this.isVorarbeiter();
  }

  /** Die persönliche Feld-Sicht "Meine Einsätze" richtet sich an die Personen vor Ort. */
  canSeeMeineEinsaetze(): boolean {
    return this.isVorarbeiter() || this.isBauleiter() || this.isMontage();
  }

  /** Die Projekt-Gesamtübersicht (Freigaben, Auswertungen) ist für Bauleiter/Admin. */
  canSeeBauleiterUebersicht(): boolean {
    return this.isBauleiter() || this.isAdmin() || this.isSuperAdmin();
  }

  /** Montage und Vorarbeiter dürfen nur lesen. Alle anderen Gruppen (inkl. Legacy "user") dürfen bearbeiten. */
  canEdit(): boolean {
    const roles = this.getRoles();
    const isRestricted = roles.includes('Montage') || roles.includes('Vorarbeiter');
    const isEditor = roles.includes('SuperAdmin') || roles.includes('Admin')
      || roles.includes('Kaufmaennisch') || roles.includes('Bauleiter')
      || roles.includes('ProjektleiterKaufmann');
    return !(isRestricted && !isEditor);
  }

  canManageUsers(): boolean {
    return this.isAdmin();
  }

  /**
   * Anzeigename direkt aus dem Token – ohne Serveranfrage.
   *
   * `getCurrentUser()` fragt den Server und ist damit ohne Verbindung
   * nutzlos. Für die offline erfassten Protokolleinträge wird der Name aber
   * gebraucht, damit im Verlauf sofort steht, WER geprüft hat. Maßgeblich
   * bleibt trotzdem der Server: beim Synchronisieren setzt er den Namen
   * erneut aus dem Token – ein manipulierter Client kann hier nichts
   * unterschieben.
   */
  getAnzeigenameAusToken(): string | null {
    const token = this.getToken();

    if (!token) {
      return null;
    }

    try {
      const decoded = this.jwtHelper.decodeToken(token);

      const name =
        decoded?.['username']
        ?? decoded?.['unique_name']
        ?? decoded?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name']
        ?? decoded?.['email'];

      return typeof name === 'string' && name.trim() ? name : null;
    } catch {
      return null;
    }
  }

  /**
   * Neue Projekte anlegen dürfen nur Admin und SuperAdmin – bewusst enger als
   * canEdit(), das auch Bauleiter und die kaufmännische Gruppe umfasst.
   * Bestehende Projekte bearbeiten und löschen bleibt bei canEdit().
   * Der Server prüft dasselbe (ProjektController.CreateProjekt).
   */
  canCreateProjekt(): boolean {
    return this.isSuperAdmin();
  }

  // ---- Zugriffssteuerung (Lager/Kunden-Zuordnung) ----

  private authHeaders(): HttpHeaders {
    return new HttpHeaders({ 'Authorization': `Bearer ${this.getToken()}` });
  }

  getMyPermissions(): Observable<any> {
    return this.http.get(`${this.accessUrl}/me`, { headers: this.authHeaders() })
      .pipe(catchError(this.handleError));
  }

  getGroups(): Observable<string[]> {
    return this.http.get<string[]>(`${this.accessUrl}/groups`, { headers: this.authHeaders() })
      .pipe(catchError(this.handleError));
  }

  getWarehouses(): Observable<{ id: number; name: string }[]> {
    return this.http.get<{ id: number; name: string }[]>(`${this.accessUrl}/warehouses`, { headers: this.authHeaders() })
      .pipe(catchError(this.handleError));
  }

  getUserAccess(userId: string): Observable<{ userId: string; warehouseIds: number[]; customerIds: number[] }> {
    return this.http.get<{ userId: string; warehouseIds: number[]; customerIds: number[] }>(
      `${this.accessUrl}/users/${userId}`, { headers: this.authHeaders() })
      .pipe(catchError(this.handleError));
  }

  setUserAccess(userId: string, warehouseIds: number[], customerIds: number[]): Observable<any> {
    return this.http.put(`${this.accessUrl}/users/${userId}`,
      { userId, warehouseIds, customerIds }, { headers: this.authHeaders() })
      .pipe(catchError(this.handleError));
  }

  getAssignableCustomers(): Observable<{ id: number; name: string }[]> {
    return this.http.get<any[]>(`${environment.apiBaseUrl}/customer`, { headers: this.authHeaders() })
      .pipe(
        map(list => (list || []).map(c => ({ id: c.id, name: c.name }))),
        catchError(this.handleError)
      );
  }

  private getToken(): string | null {
    return localStorage.getItem('token');
  }

  /**
   * Beendet die Sitzung.
   *
   * Rührt bewusst NICHT an die IndexedDB: dort liegen die offline
   * heruntergeladenen Inbetriebnahmen und – vor allem – die noch nicht
   * übertragenen Prüfungen. Ein abgelaufener Token oder ein versehentliches
   * Abmelden darf keinen Arbeitstag auf der Anlage vernichten. Nach der
   * nächsten Anmeldung läuft die Synchronisierung einfach weiter.
   */
  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('refreshTokenAusgestelltAm');
    this.token = null;
    this.laufendesRefresh = null;
    this.refreshLaeuft = false;
    this.naechsterRefreshAb = 0;
    this.clearLogoutTimer();
    this.router.navigate(['/home']);
  }

  getUserActivities(): Observable<any> {
    const token = this.getToken();
    return this.http.get(`${this.baseUrl}/activities`, {
      headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` })
    }).pipe(catchError(this.handleError));
  }

  getSignature(): Observable<{ hasSignature: boolean; url: string | null }> {
    const token = this.getToken();
    return this.http.get<{ hasSignature: boolean; url: string | null }>(`${this.baseUrl}/signature`, {
      headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` })
    }).pipe(catchError(this.handleError));
  }

  uploadSignature(dataUrl: string): Observable<{ hasSignature: boolean; url: string }> {
    const token = this.getToken();
    return this.http.post<{ hasSignature: boolean; url: string }>(`${this.baseUrl}/signature`, { dataUrl }, {
      headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` })
    }).pipe(catchError(this.handleError));
  }

  deleteSignature(): Observable<{ hasSignature: boolean }> {
    const token = this.getToken();
    return this.http.delete<{ hasSignature: boolean }>(`${this.baseUrl}/signature`, {
      headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` })
    }).pipe(catchError(this.handleError));
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Es ist ein unbekannter Fehler aufgetreten.';
    if (error.error instanceof ErrorEvent) {
      errorMessage = error.error.message;
    } else if (typeof error.error === 'string' && error.error.trim()) {
      errorMessage = error.error;
    } else if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (Array.isArray(error.error)) {
      errorMessage = error.error
        .map((err: any) => err?.description || err)
        .join(' ');
    } else if (error.error?.errors) {
      const validationMessages = Object.values(error.error.errors as Record<string, string[]>)
        .flat()
        .filter(Boolean)
        .join(' ');
      if (validationMessages) {
        errorMessage = validationMessages;
      }
    } else if (error.status) {
      const statusText = error.statusText || error.message || 'Unbekannter Fehler';
      errorMessage = `Fehler (${error.status}): ${statusText}`;
    }

    const enrichedError = new HttpErrorResponse({
      error: errorMessage,
      headers: error.headers,
      status: error.status,
      statusText: error.statusText ?? '',
      url: error.url ?? undefined  // WICHTIG: null -> undefined
    });

    return throwError(enrichedError);
  }
}
